import { cacheKeys } from "@constants/cache-keys";
import { trackAnswerEvent } from "@features/session/api/mutations";
import type { TrackAnswerEventRequest } from "@features/session/api/types";
import useSessionStore from "@features/session/hooks/useSessionStore";
import { isAxiosError } from "@helpers/lib/axios";
import {
	captureErrorToSentry,
	serializeErrorForSentry,
} from "@helpers/sentry-logger";
import useCacheKey from "@hooks/useCacheKey";
import { useMutation } from "@tanstack/react-query";
import {
	getAnswerEventRetryDelay,
	shouldRetryAnswerEvent,
} from "./answer-event-retry";

const useAnswerEvent = () => {
	const { baseCacheKey } = useCacheKey();

	const updateAnswerEventInProgress = useSessionStore(
		(state) => state.updateAnswerEventInProgress,
	);
	const mutation = useMutation({
		mutationKey: [baseCacheKey, cacheKeys.answerEvent],
		retry: shouldRetryAnswerEvent,
		retryDelay: getAnswerEventRetryDelay,
		mutationFn: (data: { answerEventPayload: TrackAnswerEventRequest }) => {
			updateAnswerEventInProgress(true);
			return trackAnswerEvent(data.answerEventPayload);
		},
		onError: (error, variables) => {
			const sentryError = new Error("Error tracking answer event");
			captureErrorToSentry(sentryError, {
				error: serializeErrorForSentry(error),
				request: serializeErrorForSentry(variables.answerEventPayload),
				...(isAxiosError(error) && {
					errorCode: error.code,
					errorMessage: error.message,
					responseStatus: error.response?.status,
					responseData: serializeErrorForSentry(error.response?.data),
				}),
			});
		},
	});

	return {
		mutateTrackAnswerEvent: mutation.mutate,
		isTrackingAnswerEvent: mutation.isPending,
	};
};

export default useAnswerEvent;
